/**
 * GUEST-FIRST public catalog: browse courses with no account (the catalog is
 * server-authoritative, REST `/api/catalog`). Enrolling saves local
 * progress; CREATING requires an account. Vibrant cards with a color per course.
 */
import { createSafeQuery } from "@/lib/api";
import { type Component, createMemo, createSignal, For, Match, Show, Switch } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import { msg } from "@lingui/core/macro";
import { BookOpen, CloudOff, Gift, Search, Sparkles, Users } from "@fleet/icons";
import { type CatalogEntryDTO, cn, courseQueryKeys, fetchCatalog, isWorkshop } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Link } from "@tanstack/solid-router";
import { SERVER_URL } from "@/lib/runtime-config";
import { Skeleton } from "@fleet/ui";
import { Mascot } from "@/components/ui/mascot";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { listEntranceProps, useListEntrance } from "@/lib/animation/list-entrance";

/** Deterministic joy color per course (cheerful but harmonic variety). */
const JOY_TONES = [
  { chip: "bg-joy-grape text-joy-grape-foreground", soft: "bg-joy-grape/12" },
  { chip: "bg-joy-sky text-joy-sky-foreground", soft: "bg-joy-sky/15" },
  { chip: "bg-joy-mint text-joy-mint-foreground", soft: "bg-joy-mint/15" },
  { chip: "bg-joy-sun text-joy-sun-foreground", soft: "bg-joy-sun/18" },
  { chip: "bg-joy-flame text-joy-flame-foreground", soft: "bg-joy-flame/12" },
  { chip: "bg-joy-berry text-joy-berry-foreground", soft: "bg-joy-berry/12" },
  { chip: "bg-joy-aqua text-joy-aqua-foreground", soft: "bg-joy-aqua/15" },
];
/** Ghost cards while loading: fill the first screen without overdoing it. */
const SKELETON_CARDS = 6;

const toneFor = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    // `?? 0` isn't defensive for the sake of it: `codePointAt` returns
    // `undefined` if the index falls out of range, the sum gives NaN and
    // `NaN >>> 0` is 0 — EVERY course would come out the same color with
    // nothing failing. A silent UI bug.
    h = (h * 31 + (id.codePointAt(i) ?? 0)) >>> 0;
  }
  return JOY_TONES[h % JOY_TONES.length]!;
};

export const CatalogView: Component = () => {
  const { t } = useI18n();
  const [q, setQ] = createSignal("");
  // B5 · N5: the "Free" lane is a HOOK, not just another filter — that's why
  // it lives up top, next to the search box, and not hidden in a facets dropdown.
  const [onlyFree, setOnlyFree] = createSignal(false);
  const catalog = createSafeQuery<CatalogEntryDTO[]>(() => ({
    queryKey: courseQueryKeys.catalogList(q()),
    queryFn: ({ signal }) => fetchCatalog(SERVER_URL, { q: q(), signal }),
    staleTime: 30_000,
    // Typing in the search box changes the queryKey: without this, every
    // keystroke sent the grid back to "no data" state and the screen
    // collapsed to skeletons between letters. With the previous results
    // still standing, the list is REPLACED when the new one arrives instead
    // of disappearing.
    placeholderData: keepPreviousData,
  }));

  /**
   * Catalog rows. `.data` is read ONLY on success, and that's the condition
   * that needs to be upheld, not an approximation.
   *
   * `createQuery` is backed by a resource: `.data` is the only field that
   * suspends, and it does so while there's no value. This screen used to
   * read it in `freeCount()`, which is evaluated outside any loading
   * branch, so the suspension dragged down even the title and the search
   * box — static, with nothing to wait for. Measured: 176ms painting
   * nothing for a 25ms response.
   *
   * The first version of this fix guarded with `!isPending`, and that
   * leaves out a case: on ERROR `isPending` is also false and `.data` still
   * has no value, so it suspended again. That's why the bug looked
   * intermittent — it depended on the request failing.
   */
  const entries = createMemo<CatalogEntryDTO[]>(() => (catalog.isSuccess ? catalog.data : []));

  // `isFree` is DERIVED by the server from the absence of a price plan: here
  // it's only filtered, never recomputed (price has a single source).
  const freeCount = createMemo(() => entries().filter((c) => c.isFree).length);
  const shown = createMemo(() => (onlyFree() ? entries().filter((c) => c.isFree) : entries()));

  // The scope does NOT include search on purpose: typing in the search box
  // refreshes the grid several times a second and re-animating it on every
  // keystroke would be exactly the flicker we want to avoid.
  const isEntering = useListEntrance({
    scope: () => (onlyFree() ? "catalog:free" : "catalog"),
    hasItems: () => shown().length > 0,
  });

  return (
    <div class="mx-auto max-w-5xl space-y-6 p-4 md:p-6">
      <div class="space-mesh flex flex-col gap-3 rounded-3xl border border-border p-5 md:flex-row md:items-center md:justify-between md:p-6">
        <div>
          <h1 class="font-display text-2xl font-extrabold tracking-tight md:text-3xl">
            {t(msg`Catalog`)}
          </h1>
          <p class="text-sm text-muted-foreground">
            {t(msg`Browse and enroll — no account needed.`)}
          </p>
        </div>
        <div class="relative">
          <Search class="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            data-testid="catalog-search"
            value={q()}
            onInput={(e) => setQ(e.currentTarget.value)}
            placeholder={t(msg`Search courses`)}
            class="w-full rounded-2xl border-2 border-border bg-surface py-2.5 pl-10 pr-3 font-medium transition-colors focus-visible:border-accent focus-visible:outline-none md:w-72"
          />
        </div>
      </div>

      {/* "Free" lane: only shows up if there's something free to show — a
          toggle that always filters down to zero is noise, not a hook. */}
      <Show when={freeCount() > 0}>
        <div class="flex flex-wrap items-center gap-2" data-testid="catalog-free-lane">
          <button
            type="button"
            data-testid="catalog-free-toggle"
            aria-pressed={onlyFree()}
            onClick={() => setOnlyFree((v) => !v)}
            class={cn(
              "flex items-center gap-1.5 rounded-full border-2 px-3.5 py-1.5 text-sm font-semibold",
              "transition-[background-color,border-color,color] duration-200",
              onlyFree()
                ? "border-transparent bg-joy-mint text-joy-mint-foreground"
                : "border-border bg-surface hover:border-accent/40",
            )}
          >
            <Gift class="h-4 w-4" />
            {t(msg`Free`)}
            <span class="opacity-70">({freeCount()})</span>
          </button>
          <p class="text-sm text-muted-foreground">
            {t(msg`Start with a free workshop — no card, no commitment.`)}
          </p>
        </div>
      </Show>

      {/* Three states that are NOT the same, which is why they render
          differently: loading, couldn't load, and nothing published. Confusing
          the last two —which is what used to happen— tells the user the
          academy is empty when what's actually going on is the server didn't answer. */}
      <Switch
        fallback={
          <div
            class="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border p-14 text-center"
            data-testid="catalog-empty"
          >
            <Mascot mood="think" class="mb-4 h-24 w-24" />
            <h3 class="font-display text-lg font-bold">{t(msg`No courses published yet`)}</h3>
            <p class="mt-1 text-sm text-muted-foreground">
              {t(msg`Create the first one from Authoring.`)}
            </p>
          </div>
        }
      >
        <Match when={catalog.isPending}>
          <div
            class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="catalog-skeleton"
            aria-busy="true"
            aria-live="polite"
            aria-label={t(msg`Loading courses`)}
          >
            <For each={Array.from({ length: SKELETON_CARDS })}>{() => <CatalogCardSkeleton />}</For>
          </div>
        </Match>

        <Match when={catalog.isError}>
          <div
            class="rounded-3xl border-2 border-dashed border-destructive/40 p-6"
            data-testid="catalog-error"
            role="alert"
          >
            <EmptyState
              icon={CloudOff}
              title={t(msg`We couldn't load the catalog`)}
              description={t(
                msg`The server didn't respond. Nothing is lost: it's a connection problem, not an empty catalog.`,
              )}
              action={
                <Button
                  data-testid="catalog-retry"
                  onClick={() => {
                    void catalog.refetch();
                  }}
                >
                  {t(msg`Try again`)}
                </Button>
              }
            />
          </div>
        </Match>

        <Match when={shown().length > 0}>
          <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-grid">
            <For each={shown()}>
              {(course, index) => (
                <CatalogCard course={course} entering={isEntering()} index={index()} />
              )}
            </For>
          </div>
        </Match>
      </Switch>
    </div>
  );
};

/**
 * Waits with the SHAPE of what's about to arrive: same container, same
 * grid, and the same blocks as the real card (chip, category, two lines of
 * title, two of description, and the metadata row). This way the grid
 * doesn't jump on resolve — the content fills the slot that was already reserved.
 *
 * `Skeleton` brings the canon's deferred appearance (140ms) and discreet
 * pulse: if the response lands sooner, the user never sees the skeleton and
 * there's no flicker. It's only announced once, on the container
 * (`aria-busy` + label): six ghost cards in the screen reader would be noise.
 */
const CatalogCardSkeleton: Component = () => (
  <div class="rounded-3xl border border-border bg-surface p-5" aria-hidden="true">
    <div class="mb-3 flex items-center gap-2">
      <Skeleton class="h-11 w-11 rounded-2xl" />
      <Skeleton class="h-5 w-20 rounded-full" />
    </div>
    <Skeleton class="h-4 w-4/5 rounded" />
    <Skeleton class="mt-2 h-4 w-3/5 rounded" />
    <Skeleton class="mt-3 h-3 w-full rounded" />
    <Skeleton class="mt-1.5 h-3 w-2/3 rounded" />
    <div class="mt-4 flex items-center gap-4">
      <Skeleton class="h-3 w-16 rounded" />
      <Skeleton class="h-3 w-10 rounded" />
      <Skeleton class="ml-auto h-3 w-24 rounded" />
    </div>
  </div>
);

const CatalogCard: Component<{ course: CatalogEntryDTO; entering: boolean; index: number }> = (
  props,
) => {
  const { t } = useI18n();
  const tone = () => toneFor(props.course.id);
  const entrance = () => listEntranceProps(props.entering, props.index);
  return (
    <Link
      to="/catalog/$courseId"
      params={{ courseId: props.course.id }}
      // `transition-all` animated EVERY property (including ones that force
      // layout). Explicit list: transform is compositor-only and the shadow
      // lives on a small card, not on a full-screen surface.
      class={cn(
        "group relative block overflow-hidden rounded-3xl border border-border bg-surface p-5 shadow-sm",
        "transition-[transform,border-color,box-shadow] duration-200 ease-out",
        "hover:-translate-y-1 hover:border-accent/40 hover:shadow-lg active:scale-[0.99]",
        entrance().class,
      )}
      style={entrance().style}
      data-testid="catalog-card"
    >
      <div
        class={`pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full ${tone().soft}`}
      />
      <div class="relative mb-3 flex items-center gap-2">
        <div
          class={`flex h-11 w-11 items-center justify-center rounded-2xl shadow-sm transition-transform group-hover:scale-105 ${tone().chip}`}
        >
          <BookOpen class="h-5 w-5" />
        </div>
        <Show when={props.course.category}>
          <span class={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${tone().soft}`}>
            {props.course.category}
          </span>
        </Show>
        {/* B5 · the three hook signals, in order of commercial strength:
            free > workshop > has a sample class. Never all three at once: a
            free course doesn't need to advertise that it has a sample. */}
        <Show when={props.course.isFree}>
          <span
            data-testid="catalog-free-badge"
            class="flex items-center gap-1 rounded-full bg-joy-mint px-2.5 py-0.5 text-xs font-bold text-joy-mint-foreground"
          >
            <Gift class="h-3 w-3" />
            {isWorkshop(props.course.programKind) ? t(msg`Free workshop`) : t(msg`Free`)}
          </span>
        </Show>
        <Show when={!props.course.isFree && props.course.freePreviewLessons > 0}>
          <span
            data-testid="catalog-preview-badge"
            class="flex items-center gap-1 rounded-full bg-joy-sun px-2.5 py-0.5 text-xs font-bold text-joy-sun-foreground"
          >
            <Sparkles class="h-3 w-3" />
            {t(msg`{count} free sample classes`, { count: props.course.freePreviewLessons })}
          </span>
        </Show>
      </div>
      <h3 class="relative line-clamp-2 font-display font-bold group-hover:text-accent">
        {props.course.title}
      </h3>
      <Show when={props.course.description}>
        <p class="relative mt-1 line-clamp-2 text-sm text-muted-foreground">
          {props.course.description}
        </p>
      </Show>
      <div class="relative mt-4 flex items-center gap-4 text-xs font-medium text-muted-foreground">
        <span>{t(msg`{count} lessons`, { count: props.course.lessonCount })}</span>
        <span class="flex items-center gap-1">
          <Users class="h-3 w-3" /> {props.course.enrolledCount}
        </span>
        <Show when={props.course.authorName}>
          {(name) => <span class="ml-auto truncate">{t(msg`by {name}`, { name: name() })}</span>}
        </Show>
      </div>
    </Link>
  );
};
